import { useState } from 'react'
import styled, { css } from 'styled-components'
import ConfettiExplosion from 'react-confetti-explosion'
import { useSelector } from 'react-redux'
import { Box } from '../../../UI/Box'
import { theseus } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { Checkbox } from '../../../../page_components/DropDetails/ChapterView'
import TaskBody from '../../TaskBody'
import {
  SubTask,
  Task,
} from '../../../../page_components/PoolDetails/Missions/types'
import AnimatedLoader from '../../../AnimatedLoader'
import { ThemedButton } from '../../../ThemedButton'
import { useSearchParams } from '../../../../hooks/useSearchParams'
import {
  TaskAllocationMaxAmount,
  useGetFormattedTaskRewardLabels,
} from '../../TaskAllocationAmount'
import { Info } from '../../styles'
import TaskDescription from '../../TaskDescription'
import MissionAction from '../../MissionAction'
import SharedTaskCard from '../../../Library/Card/SharedTaskCard'

const CheckContainer = styled(Box)<{ isVisible: boolean }>`
  max-width: 0px;
  overflow: hidden;
  opacity: 0;
  transition: 0.3s ease-in-out;
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      max-width: 28px;
      padding-right: 8px;
    `}
`

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  rewardDisplay: string
}

const ZapValueIndexTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  rewardDisplay,
}: Props) => {
  const { publicName, instructions } = subtask
  const title = publicName ?? 'ZAP Value Index (ZVI) in partnership with Nansen'
  const description =
    instructions ??
    'Verify your wallet to earn a reputation score based on your on-chain activity, including active months, tokens transacted, and cumulative gas spent.'

  const [isLoadingVerify, setIsLoadingVerify] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const project = useSelector(selectCurrentProject)

  const { updateSearchParam } = useSearchParams()

  function selfDestruct(ms = 2000) {
    setTimeout(() => {
      updateSearchParam('active_mission', '')
    }, ms)
  }

  async function validate() {
    try {
      setError(null)
      setIsLoadingVerify(true)

      const { data } = await theseus.post(
        `/task-submission/b2c/zap-value-index`,
        {
          subtaskId: subtask.id,
          projectId: project.id,
          taskId,
        },
      )

      if (!data?.message)
        throw new Error(
          'Your ZVI score is less than 33. Please try again later.',
        )
      const { message } = data
      if (!message) throw new Error('Bad response')
      if (message !== 'success') throw new Error(message)
      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error?.message)
      setError(error.message)
    } finally {
      setIsLoadingVerify(false)
    }
  }

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={error}
      extra={
        <TaskAllocationMaxAmount
          taskId={taskId}
          rewardDisplay={rewardDisplay}
        />
      }
      actions={
        <Box fu aw>
          <ThemedButton
            onClick={validate}
            disabled={isComplete || isLoadingVerify}
            $styleType="primary"
          >
            {isComplete && <ConfettiExplosion zIndex={10000} />}
            <CheckContainer isVisible={isComplete}>
              <Checkbox />
            </CheckContainer>
            {isLoadingVerify ? (
              <AnimatedLoader size={14} />
            ) : isComplete ? (
              'Verified'
            ) : (
              'Verify'
            )}
          </ThemedButton>
        </Box>
      }
    />
  )
}

export const Icon = styled.img<{ smallMargin?: boolean }>`
  width: 18px;
  height: 18px;
`

export const ResetInfo = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`

export type CardProps = {
  task: Task
  coreTask?: SubTask
  projectId?: number
  isComplete: boolean
  projectStatus?: 'ended' | 'active' | 'coming-soon'
  rewardDisplay?: string
  onClick?: () => void
  isExpired?: boolean
}

export const ZapValueIndexTaskCard = ({
  task,
  isComplete,
  projectStatus,
  rewardDisplay,
  isExpired,
  onClick,
}: CardProps) => {
  const { internalName: description, multiplierReward } = task

  const getFormattedReward = useGetFormattedTaskRewardLabels({
    taskId: task.id,
  })
  const { formattedMaxReward, formattedReward } = getFormattedReward()

  const isDisabled = isComplete || projectStatus !== 'active' || isExpired

  return (
    <SharedTaskCard
      isFaded={isDisabled}
      isExpired={isExpired}
      isComplete={isComplete}
      task={task}
      formattedReward={formattedReward}
      multiplier={multiplierReward}
      onClick={isDisabled ? () => null : onClick}
      xpTosShow={formattedMaxReward}
    >
      <Box g={40} ac>
        <Box dc g={6}>
          <TaskDescription>{description}</TaskDescription>
          <Box g={4} ac>
            <Info type="primary">
              <span className="reward-amount">
                <span className="allocation-amount">
                  {isComplete ? formattedReward : formattedMaxReward}
                </span>
              </span>
            </Info>
          </Box>
        </Box>
      </Box>
      <MissionAction taskId={task.id} projectStatus={projectStatus} />
    </SharedTaskCard>
  )
}

export default ZapValueIndexTask
